/*
 *  Kanlibs/KAN_BusyHold.js
 *  Project: Kanoogi
 *
 *  HoldBusy(reason) - mark an operation a page reload would lose (a recorded clip on
 *  its way to the server, an upload in flight) for the platform's version watcher
 *  (Kanlibs/KAN_VersionWatch.js), which will not reload the page while any hold is
 *  open. Returns release(); call it exactly when the work is safe (sent, or failed
 *  visibly). WhileBusy(reason, fn) does both around an async function.
 *
 *  Why this exists beside the watcher's DOM guards: some work lives only in memory
 *  for a while - a recorded clip after its recorder modal has closed, before its
 *  optimistic message is in the DOM (an E2EE chat can spend a network round-trip
 *  encrypting the caption first). No selector can see that; a hold can, from the
 *  first synchronous line of the send to its last.
 *
 *  It imports NOTHING, on purpose. Shared libraries (KAN_Convo, KAN_KanCom) call it,
 *  and a library that imported the watcher would drag it into any page that ever
 *  shares that library - a game client included, which must never reload mid-game
 *  (tests/version-watch-wiring.test.mjs). On a page with no watcher the holds are
 *  simply never read.
 *
 *  Copyright 2026 Kanoogi Inc. | All Rights Reserved.
 */

"use strict";

const HOLDS='KanBusyHolds';

function Holds(win)
	{
	if (!win) return(null);
	if (!(win[HOLDS] instanceof Map)) win[HOLDS]=new Map();
	return(win[HOLDS]);
	}

function Win()
	{
	return((typeof window!=='undefined')?window:null);
	}

// HoldBusy() - open a hold. Returns release(), which is idempotent.
export function HoldBusy(reason, win=Win())
	{
	const holds=Holds(win);
	if (!holds) return(()=>{});
	const token={};
	holds.set(token,String(reason||'busy'));
	let open=true;
	return(()=>
		{
		if (!open) return;
		open=false;
		holds.delete(token);
		});
	}

// WhileBusy() - hold for exactly as long as fn's promise is pending, success or
// failure. The hold opens synchronously, before fn runs.
export async function WhileBusy(reason, fn, win=Win())
	{
	const release=HoldBusy(reason,win);
	try { return(await fn()); }
	finally { release(); }
	}

// HeldReason() - the oldest open hold's reason, or '' when nothing is held. This is
// what the watcher's built-in 'held' guard reads.
export function HeldReason(win=Win())
	{
	const holds=win && win[HOLDS];
	if (!(holds instanceof Map)) return('');
	for (const reason of holds.values()) return(reason);
	return('');
	}
